/**
 * DagsterPage — embeds the Dagit (Dagster webserver) UI inside the tenant
 * console.
 *
 * The Dagit upstream is launched with --path-prefix=/admin/dagit so the
 * tenant-api reverse proxy at /admin/dagit/* serves it under the same
 * origin as this app. Same-origin means the iframe inherits cookies without
 * CORS gymnastics.
 *
 * Auth: we never put the JWT in the iframe URL (that leaks it to browser
 * history, server/proxy logs, and the Referer header). Instead we POST the
 * bearer token to /admin/dagit/session — fetch CAN set the Authorization
 * header, an iframe src cannot — which sets a short-lived, HttpOnly,
 * Dagit-scoped session cookie. The iframe then loads with no token in the
 * URL and authenticates via that cookie.
 */
import React, { useEffect, useState } from 'react';

import { isDagitEnabled } from '../services/dagsterLink';

export default function DagsterPage() {
  // loading | ready | error | disabled
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    // Most deployments run with the reverse proxy unmounted (tenant-api
    // DAGIT_PROXY_ENABLED=false), so the iframe would load a 404. Say so
    // instead, and don't bother minting a session.
    if (!isDagitEnabled()) {
      setStatus('disabled');
      return undefined;
    }
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : '';
    if (!token) {
      setStatus('error');
      return undefined;
    }
    (async () => {
      try {
        const resp = await fetch('/admin/dagit/session', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'same-origin',
        });
        if (!cancelled) {setStatus(resp.ok ? 'ready' : 'error');}
      } catch (e) {
        if (!cancelled) {setStatus('error');}
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const containerStyle = {
    height: 'calc(100vh - 64px)',
    width: '100%',
    background: 'var(--theme-bg, #fff)',
  };

  if (status === 'loading') {
    return <div style={{ ...containerStyle, display: 'grid', placeItems: 'center' }}>Loading Dagster…</div>;
  }
  if (status === 'disabled') {
    return (
      <div style={{ ...containerStyle, display: 'grid', placeItems: 'center', padding: 24 }}>
        <div style={{ maxWidth: 520, textAlign: 'center' }}>
          <h3>The Dagster UI is not enabled on this deployment</h3>
          <p style={{ color: 'var(--theme-text-muted, #888)' }}>
            Ingestion is unaffected — sync runs, logs, schedules and backfills are
            all available on the Pipeline Sources page. An administrator can turn
            this page on for troubleshooting.
          </p>
        </div>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div style={{ ...containerStyle, display: 'grid', placeItems: 'center' }}>
        Unable to open Dagster — admin access is required.
      </div>
    );
  }
  return (
    <div style={containerStyle}>
      <iframe
        title="Dagster"
        src="/admin/dagit/"
        style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
      />
    </div>
  );
}
